/**
 * WebCrypto + IndexedDB: owner keys live here and cannot leave.
 *
 * Three levels of protection, and the UI names which one is actually in force:
 *
 *   none          the key is a non-extractable CryptoKey. JavaScript can use
 *                 it but never read it. Anyone at the unlocked browser is in.
 *   webauthn-gate the same, plus a Windows Hello prompt before use. A human is
 *                 in the loop; the stored key itself is not encrypted.
 *   webauthn-prf  the private key is stored ENCRYPTED with a secret only the
 *                 authenticator can derive. Without a successful biometric
 *                 check the record is ciphertext and nothing can use it.
 *
 * Full encryption is chosen at import, because it needs the private key
 * material, which by design we no longer have afterwards.
 */
import {
  ENTERED_FLAG, IDB_NAME, IDB_STORE, IDB_VERSION,
  KEY_ALGORITHM, SIGN_ALGORITHM, SIGNED_OUT_FLAG,
} from '../../shared/constants.ts';
import { base64UrlToBytes, bytesToBase64Url, utf8 } from '../../shared/base64.ts';
import { credentialSchema, type Credential } from '../../shared/schema/credential.ts';
import {
  enroll, keyFromSecret, platformAuthenticatorAvailable, verify,
  type Enrollment, type Protection,
} from './webauthn.ts';

export interface KeySummary {
  kid: string;
  owner: string;
  label: string;
  app: string;
  install: string;
  importedAt: string;
  protection: Protection;
  active: boolean;
}

interface StoredRecord {
  kid: string;
  owner: string;
  label: string;
  app: string;
  install: string;
  importedAt: string;
  protection: Protection;
  /** Present for 'none' and 'webauthn-gate'. */
  key?: CryptoKey | undefined;
  /** Present for 'webauthn-prf': the private JWK, encrypted. */
  sealed?: string | undefined;
  enrollment?: Enrollment | undefined;
}

/** The usable key for this page session. Never persisted in this form. */
export interface UnlockedKey {
  kid: string;
  owner: string;
  label: string;
  app: string;
  install: string;
  /** Carried along so the transport knows what may be resumed after a reload. */
  protection: Protection;
  key: CryptoKey;
}

const ACTIVE_POINTER = '__active__';
const LEGACY_ID = 'owner';

// ── persistent sign-out ────────────────────────────────────────────────────

/**
 * A logout that a page reload undoes is not a logout. The flag lives in
 * localStorage so it survives F5, and only an explicit "Войти" clears it.
 */
export function isSignedOut(): boolean {
  try {
    return localStorage.getItem(SIGNED_OUT_FLAG) === '1';
  } catch {
    return false;
  }
}

export function markSignedOut(): void {
  try {
    localStorage.setItem(SIGNED_OUT_FLAG, '1');
  } catch {
    /* private mode: the in-memory state still applies for this tab */
  }
}

export function clearSignedOut(): void {
  try {
    localStorage.removeItem(SIGNED_OUT_FLAG);
  } catch {
    /* ignore */
  }
}

// ── resuming after a reload ────────────────────────────────────────────────

/**
 * Pressing F5 is not logging in again, so it should not feel like it. What may
 * be resumed depends on how the key is protected, and the difference is not a
 * preference - it follows from what is physically possible:
 *
 *   none          the CryptoKey is on disk. Resume across reloads and browser
 *                 restarts; only «Выход» ends it.
 *   webauthn-gate the promise is «спросить Hello у пришедшего». A reload is
 *                 not an arrival, so the mark lives in sessionStorage: same
 *                 tab keeps going, a new tab asks again.
 *   webauthn-prf  the key exists only as ciphertext at rest; the plaintext
 *                 lived in page memory and is gone. Nothing to resume - the
 *                 authenticator has to be asked, and that needs a click.
 */
function markStore(protection: Protection): Storage | null {
  if (protection === 'webauthn-prf') return null;
  return protection === 'none' ? localStorage : sessionStorage;
}

export function markEntered(protection: Protection): void {
  try {
    markStore(protection)?.setItem(ENTERED_FLAG, '1');
  } catch {
    /* private mode: the tab still works, the next reload just asks again */
  }
}

export function hasEntered(protection: Protection): boolean {
  try {
    return markStore(protection)?.getItem(ENTERED_FLAG) === '1';
  } catch {
    return false;
  }
}

export function clearEntered(): void {
  try {
    localStorage.removeItem(ENTERED_FLAG);
    sessionStorage.removeItem(ENTERED_FLAG);
  } catch {
    /* ignore */
  }
}

// ── IndexedDB plumbing ─────────────────────────────────────────────────────

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(IDB_STORE)) {
        req.result.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('cannot open IndexedDB'));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, mode);
    let result: T;
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    Promise.resolve(fn(tx.objectStore(IDB_STORE)))
      .then((value) => {
        result = value;
      })
      .catch((err: unknown) => {
        tx.abort();
        reject(err);
      });
  });
}

function req<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

const available = (): boolean => typeof indexedDB !== 'undefined';

interface Snapshot {
  keys: StoredRecord[];
  activeKid: string | null;
}

async function readAll(store: IDBObjectStore): Promise<Snapshot> {
  const ids = await req(store.getAllKeys());
  const values = await req(store.getAll());

  let activeKid: string | null = null;
  const keys: StoredRecord[] = [];

  for (let i = 0; i < ids.length; i++) {
    const id = String(ids[i]);
    const value = values[i] as unknown;
    if (id === ACTIVE_POINTER) {
      activeKid = (value as { kid?: string }).kid ?? null;
      continue;
    }
    if (!value || typeof value !== 'object' || !('kid' in value)) continue;
    const record = value as StoredRecord;
    record.protection ??= 'none';

    if (id === LEGACY_ID && record.kid) {
      store.put(record, record.kid);
      store.delete(LEGACY_ID);
    }
    keys.push(record);
  }

  keys.sort((a, b) => a.importedAt.localeCompare(b.importedAt));
  return { keys, activeKid };
}

function summarize(record: StoredRecord, activeKid: string | null): KeySummary {
  return {
    kid: record.kid,
    owner: record.owner,
    label: record.label,
    app: record.app,
    install: record.install,
    importedAt: record.importedAt,
    protection: record.protection,
    active: record.kid === activeKid,
  };
}

export async function listKeys(): Promise<KeySummary[]> {
  if (!available()) return [];
  try {
    const { keys, activeKid } = await withStore('readwrite', readAll);
    const effective = activeKid ?? keys[0]?.kid ?? null;
    return keys.map((k) => summarize(k, effective));
  } catch {
    return [];
  }
}

async function activeRecord(): Promise<StoredRecord | null> {
  if (!available()) return null;
  try {
    const { keys, activeKid } = await withStore('readwrite', readAll);
    if (keys.length === 0) return null;
    return keys.find((k) => k.kid === activeKid) ?? keys[0] ?? null;
  } catch {
    return null;
  }
}

export async function activeSummary(): Promise<KeySummary | null> {
  const record = await activeRecord();
  return record ? summarize(record, record.kid) : null;
}

export async function setActiveKey(kid: string): Promise<void> {
  if (!available()) return;
  await withStore('readwrite', (store) => {
    store.put({ kid }, ACTIVE_POINTER);
  });
}

export async function deleteKey(kid: string): Promise<void> {
  if (!available()) return;
  lock();
  clearEntered();
  await withStore('readwrite', async (store) => {
    store.delete(kid);
    const { keys, activeKid } = await readAll(store);
    if (activeKid !== kid) return;
    const next = keys.find((k) => k.kid !== kid);
    if (next) store.put({ kid: next.kid }, ACTIVE_POINTER);
    else store.delete(ACTIVE_POINTER);
  });
}

// ── unlocking ──────────────────────────────────────────────────────────────

/** Held only in memory, only for this page session. */
let unlocked: UnlockedKey | null = null;

export function currentUnlocked(): UnlockedKey | null {
  return unlocked;
}

export function lock(): void {
  unlocked = null;
}

export class CredentialError extends Error {}

async function sealJwk(jwk: object, aesKey: CryptoKey): Promise<string> {
  const iv = new Uint8Array(12);
  crypto.getRandomValues(iv);
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv }, aesKey, utf8(JSON.stringify(jwk)),
  );
  return `v1.${bytesToBase64Url(iv)}.${bytesToBase64Url(new Uint8Array(ct))}`;
}

async function openJwk(sealed: string, aesKey: CryptoKey): Promise<JsonWebKey> {
  const [version, ivPart, ctPart] = sealed.split('.');
  if (version !== 'v1' || !ivPart || !ctPart) throw new CredentialError('Повреждённая запись ключа.');
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: base64UrlToBytes(ivPart) }, aesKey, base64UrlToBytes(ctPart),
  );
  return JSON.parse(new TextDecoder().decode(plain)) as JsonWebKey;
}

/**
 * Continues an existing session after a reload, without any prompt. Refuses
 * for an encrypted key: there the plaintext genuinely no longer exists, and
 * pretending otherwise would just fail later in a more confusing place.
 */
export async function resumeActiveKey(): Promise<UnlockedKey> {
  const record = await activeRecord();
  if (!record) throw new CredentialError('В этом браузере нет ни одного ключа.');
  if (record.protection === 'webauthn-prf') {
    throw new CredentialError('Ключ зашифрован: нужен запрос Windows Hello.');
  }
  if (!record.key) throw new CredentialError('Запись ключа повреждена, добавьте его заново.');
  unlocked = { ...record, key: record.key };
  return unlocked;
}

/**
 * Makes the active key usable, prompting for biometrics when the key is
 * protected. Called from an explicit user action, so the owner always decides
 * whether to enter.
 */
export async function unlockActiveKey(): Promise<UnlockedKey> {
  const record = await activeRecord();
  if (!record) throw new CredentialError('В этом браузере нет ни одного ключа.');

  if (record.protection === 'none') {
    if (!record.key) throw new CredentialError('Запись ключа повреждена, добавьте его заново.');
    unlocked = { ...record, key: record.key };
    return unlocked;
  }

  if (!record.enrollment) throw new CredentialError('Ключ помечен защищённым, но регистрация утеряна.');

  const secret = await verify(record.enrollment);

  if (record.protection === 'webauthn-gate') {
    if (!record.key) throw new CredentialError('Запись ключа повреждена, добавьте его заново.');
    unlocked = { ...record, key: record.key };
    return unlocked;
  }

  if (!secret || !record.sealed) throw new CredentialError('Не удалось расшифровать ключ.');
  const aesKey = await keyFromSecret(secret);
  const jwk = await openJwk(record.sealed, aesKey);
  const key = await crypto.subtle.importKey(
    'jwk', { ...jwk, key_ops: ['sign'], ext: false }, KEY_ALGORITHM, false, ['sign'],
  );
  unlocked = { ...record, key };
  return unlocked;
}

// ── import ─────────────────────────────────────────────────────────────────

export async function protectionAvailable(): Promise<boolean> {
  return platformAuthenticatorAvailable();
}

export async function importCredential(
  json: string,
  options: { protect: boolean } = { protect: false },
): Promise<KeySummary> {
  let parsedJson: unknown;
  try {
    parsedJson = JSON.parse(json);
  } catch {
    throw new CredentialError('Это не похоже на JSON. Вставьте содержимое файла целиком.');
  }

  const parsed = credentialSchema.safeParse(parsedJson);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    throw new CredentialError(
      `Файл не похож на ключ доступа${first ? `: ${first.path.join('.')} ${first.message}` : ''}.`,
    );
  }
  const credential: Credential = parsed.data;

  let key: CryptoKey;
  try {
    key = await crypto.subtle.importKey(
      'jwk',
      { ...credential.privateKeyJwk, key_ops: ['sign'], ext: false },
      KEY_ALGORITHM,
      false, // ← неизвлекаемый: прочитать ключ обратно нельзя уже никогда
      ['sign'],
    );
  } catch {
    throw new CredentialError('Ключ не удалось импортировать: файл повреждён.');
  }
  if (key.extractable) {
    throw new CredentialError('Браузер импортировал ключ извлекаемым. Вход отменён.');
  }

  const base: StoredRecord = {
    kid: credential.kid,
    owner: credential.owner,
    label: credential.label,
    app: credential.app,
    install: crypto.randomUUID(),
    importedAt: new Date().toISOString(),
    protection: 'none',
    key,
  };

  let record = base;
  if (options.protect) {
    const enrollment = await enroll(credential.kid, credential.owner);
    if (enrollment.protection === 'webauthn-prf') {
      const secret = await verify(enrollment);
      if (!secret) throw new CredentialError('Аутентификатор не выдал ключ шифрования.');
      const aesKey = await keyFromSecret(secret);
      // The CryptoKey is deliberately dropped: from here on the only copy on
      // disk is ciphertext that this authenticator alone can open.
      record = {
        ...base,
        key: undefined,
        protection: 'webauthn-prf',
        enrollment,
        sealed: await sealJwk(credential.privateKeyJwk, aesKey),
      };
    } else {
      record = { ...base, protection: 'webauthn-gate', enrollment };
    }
  }

  await withStore('readwrite', (store) => {
    store.put(record, record.kid);
    store.put({ kid: record.kid }, ACTIVE_POINTER);
  });

  clearSignedOut();
  return summarize(record, record.kid);
}

/**
 * Adds a Hello prompt to a key that was imported without one. Only the gate
 * level is reachable here: encrypting the key needs its private material,
 * which we no longer hold. Re-importing the file gives full protection.
 */
export async function addGate(kid: string): Promise<KeySummary> {
  const { keys } = await withStore('readwrite', readAll);
  const record = keys.find((k) => k.kid === kid);
  if (!record) throw new CredentialError('Ключ не найден.');
  if (record.protection !== 'none') throw new CredentialError('Этот ключ уже защищён.');

  const enrollment = await enroll(record.kid, record.owner);
  const updated: StoredRecord = { ...record, protection: 'webauthn-gate', enrollment };
  await withStore('readwrite', (store) => {
    store.put(updated, updated.kid);
  });
  return summarize(updated, updated.kid);
}

export async function signPayload(key: CryptoKey, payload: string): Promise<string> {
  const signature = await crypto.subtle.sign(SIGN_ALGORITHM, key, utf8(payload));
  return bytesToBase64Url(new Uint8Array(signature));
}
